
import React from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Cookie,
    Database,
    FileText,
    LockKeyhole,
    Mail,
    Shield,
    ShieldCheck,
    Sparkles,
    UserRoundCheck,
    Users,
} from "lucide-react";

import hotelImg from "../assests/images/hotel-bg.jpg";

const sections = [
    {
        title: "Introduction",
        icon: FileText,
        text: "We value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data.",
    },
    {
        title: "Information We Collect",
        icon: Database,
        list: [
            "Personal details such as name, email and phone number",
            "Booking and travel preferences",
            "Payment information handled through secure systems",
            "Usage data including cookies and analytics information",
        ],
    },
    {
        title: "How We Use Your Information",
        icon: Users,
        list: [
            "Process bookings and payments",
            "Improve our website and user experience",
            "Send booking confirmations and relevant communications",
            "Provide customer support and respond to enquiries",
        ],
    },
    {
        title: "Data Security",
        icon: LockKeyhole,
        text: "We use reasonable security measures, including secure systems and appropriate safeguards, to help protect your personal information from unauthorized access, misuse or disclosure.",
    },
    {
        title: "Sharing of Information",
        icon: ShieldCheck,
        text: "We do not sell your personal information. Information may be shared with trusted service providers or partners when necessary to provide our services, process bookings or support your requests.",
    },
    {
        title: "Cookies",
        icon: Cookie,
        text: "We use cookies and similar technologies to improve your browsing experience, understand website usage and support certain website functionality. You can manage cookies through your browser settings.",
    },
    {
        title: "Your Rights",
        icon: UserRoundCheck,
        text: "You may request access to, correction of, or deletion of your personal information where applicable. You can contact our team if you have questions or requests regarding your personal data.",
    },
    {
        title: "Contact Us",
        icon: Mail,
        text: "If you have any questions or concerns about this Privacy Policy or how your information is handled, please contact our support team.",
    },
];

const trustPoints = [
    "Secure information handling",
    "Trusted service partners",
    "Privacy-focused practices",
];

function PolicySection({ section, index, last }) {
    const Icon = section.icon;

    return (
        <section
            id={`privacy-section-${index}`}
            className={`group scroll-mt-28 ${last ? "" : "mb-9 border-b border-[#E3ECE6] pb-9"
                }`}
        >
            <div className="flex items-start gap-4 sm:gap-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#168B43] transition-all duration-300 group-hover:bg-[#168B43] group-hover:text-white">
                    <Icon size={22} />
                </div>

                <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#168B43]">
                        Section {String(index + 1).padStart(2, "0")}
                    </p>

                    <h3 className="mt-2 text-[22px] font-extrabold tracking-tight text-[#17324D] sm:text-[26px]">
                        {section.title}
                    </h3>

                    {section.text && (
                        <p className="mt-4 text-[14px] leading-8 text-[#718096]">
                            {section.text}
                        </p>
                    )}

                    {section.list && (
                        <div className="mt-5 space-y-3">
                            {section.list.map((item) => (
                                <div
                                    key={item}
                                    className="flex items-start gap-3 rounded-xl bg-[#F8FBF9] p-3.5"
                                >
                                    <CheckCircle2
                                        size={18}
                                        className="mt-1 shrink-0 text-[#168B43]"
                                    />

                                    <p className="text-[14px] leading-7 text-[#617589]">
                                        {item}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export default function PrivacyPolicy() {
    return (
        <main className="min-h-screen overflow-hidden bg-[#F5FAF7] text-[#17324D]">
            {/* HERO */}
            <section className="relative isolate overflow-hidden bg-[#064E3B] pt-28 text-white sm:pt-32">
                <img
                    src={hotelImg}
                    alt=""
                    className="absolute inset-0 -z-20 h-full w-full object-cover"
                />

                <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#032F28]/95 via-[#064E3B]/90 to-[#064E3B]/65" />

                <div className="mx-auto max-w-[1536px] px-4 pb-16 pt-4 sm:px-6 sm:pb-20 lg:px-8">
                    <nav
                        aria-label="Breadcrumb"
                        className="flex items-center gap-2 text-xs font-semibold text-white/70"
                    >
                        <Link to="/" className="hover:text-white">
                            Home
                        </Link>

                        <ChevronRight size={14} />

                        <span className="text-[#B6F2CB]">
                            Privacy Policy
                        </span>
                    </nav>

                    <div className="mt-11 max-w-[850px]">
                        <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#B6F2CB]">
                            <ShieldCheck size={15} />
                            Privacy & Protection
                        </span>

                        <h1 className="mt-6 text-[42px] font-extrabold leading-[1.1] tracking-tight text-white sm:text-[58px] lg:text-[68px]">
                            Your Privacy.
                            <br />
                            <span className="text-[#A5E8B8]">
                                Our Commitment.
                            </span>
                        </h1>

                        <p className="mt-6 max-w-[650px] text-[14px] leading-8 text-white/80 sm:text-base">
                            Your privacy matters to us. Learn how we
                            collect, use and protect your information
                            when you explore hotels and use our services.
                        </p>
                    </div>
                </div>
            </section>

            {/* INTRODUCTION */}
            <section className="py-14 sm:py-20">
                <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
                    <div className="grid gap-6 lg:grid-cols-[1.4fr_0.6fr]">
                        <div className="overflow-hidden rounded-[24px] border border-[#DCEAE1] bg-white p-7 shadow-sm sm:p-10 lg:p-12">
                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E8F7EE] text-[#168B43]">
                                <Shield size={27} />
                            </div>

                            <p className="mt-7 text-[11px] font-extrabold uppercase tracking-[0.17em] text-[#168B43]">
                                Your Information Matters
                            </p>

                            <h2 className="mt-3 text-[32px] font-extrabold leading-tight tracking-tight sm:text-[43px]">
                                Privacy Built On{" "}
                                <span className="text-[#168B43]">
                                    Trust.
                                </span>
                            </h2>

                            <p className="mt-6 text-sm leading-8 text-[#718096] sm:text-[15px]">
                                We are committed to protecting your
                                personal information and maintaining
                                your trust. This Privacy Policy explains
                                how we collect, use and safeguard the
                                information you provide when using our
                                website and services.
                            </p>

                            <p className="mt-4 text-sm leading-8 text-[#718096] sm:text-[15px]">
                                We believe that a comfortable travel
                                experience begins with confidence,
                                transparency and responsible handling
                                of your information.
                            </p>
                        </div>

                        <div className="relative overflow-hidden rounded-[24px] bg-[#064E3B] p-8 text-white sm:p-10">
                            <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full border border-white/10" />

                            <div className="relative">
                                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 text-[#A5E8B8]">
                                    <LockKeyhole size={27} />
                                </div>

                                <p className="mt-7 text-[11px] font-extrabold uppercase tracking-[0.16em] text-[#B6F2CB]">
                                    Our Commitment
                                </p>

                                <h3 className="mt-3 text-[28px] font-extrabold leading-tight text-white">
                                    Responsible
                                    <br />
                                    <span className="text-[#A5E8B8]">
                                        Data Handling.
                                    </span>
                                </h3>

                                <p className="mt-4 text-sm leading-7 text-white/75">
                                    We take reasonable measures to help keep
                                    your personal information secure and
                                    handle it responsibly.
                                </p>

                                <div className="mt-8 space-y-4 border-t border-white/20 pt-6">
                                    {trustPoints.map((point) => (
                                        <div
                                            key={point}
                                            className="flex items-center gap-3"
                                        >
                                            <CheckCircle2
                                                size={18}
                                                className="shrink-0 text-[#A5E8B8]"
                                            />

                                            <span className="text-sm text-white/85">
                                                {point}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* POLICY CONTENT */}
            <section className="bg-white py-14 sm:py-20">
                <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
                    <div className="mb-10 max-w-[760px]">
                        <p className="text-[11px] font-extrabold uppercase tracking-[0.17em] text-[#168B43]">
                            Understanding Our Practices
                        </p>

                        <h2 className="mt-3 text-[32px] font-extrabold tracking-tight sm:text-[44px]">
                            Privacy Policy{" "}
                            <span className="text-[#168B43]">
                                Details.
                            </span>
                        </h2>

                        <p className="mt-4 text-sm leading-8 text-[#718096]">
                            Explore how your information is collected,
                            used and protected when you interact with
                            our website.
                        </p>
                    </div>

                    <div className="grid items-start gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
                        {/* SIDEBAR */}
                        <aside className="lg:sticky lg:top-24">
                            <div className="rounded-[22px] border border-[#DCEAE1] bg-[#F8FBF9] p-5">
                                <p className="px-3 pt-2 text-[11px] font-extrabold uppercase tracking-[0.15em] text-[#168B43]">
                                    On This Page
                                </p>

                                <h3 className="px-3 pb-4 pt-2 text-lg font-extrabold">
                                    Quick Navigation
                                </h3>

                                <details className="group lg:hidden">
                                    <summary className="flex cursor-pointer items-center justify-between rounded-xl bg-white px-4 py-3 text-sm font-bold">
                                        View Sections
                                        <ChevronDown
                                            size={17}
                                            className="transition group-open:rotate-180"
                                        />
                                    </summary>

                                    <nav className="mt-3 space-y-1">
                                        {sections.map((section, index) => (
                                            <a
                                                key={section.title}
                                                href={`#privacy-section-${index}`}
                                                className="flex items-center gap-3 rounded-xl px-3 py-3 text-xs font-bold text-[#617589] hover:bg-white hover:text-[#168B43]"
                                            >
                                                <span className="text-[#168B43]">
                                                    {String(index + 1).padStart(2, "0")}
                                                </span>
                                                {section.title}
                                            </a>
                                        ))}
                                    </nav>
                                </details>

                                <nav
                                    aria-label="Privacy policy sections"
                                    className="hidden space-y-1 lg:block"
                                >
                                    {sections.map((section, index) => (
                                        <a
                                            key={section.title}
                                            href={`#privacy-section-${index}`}
                                            className="group flex items-center gap-3 rounded-xl px-3 py-3 text-[12px] font-bold text-[#617589] transition hover:bg-white hover:text-[#168B43]"
                                        >
                                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-[10px] font-extrabold text-[#168B43] group-hover:bg-[#168B43] group-hover:text-white">
                                                {String(index + 1).padStart(2, "0")}
                                            </span>

                                            <span className="flex-1">
                                                {section.title}
                                            </span>

                                            <ChevronRight size={14} />
                                        </a>
                                    ))}
                                </nav>

                                <div className="mt-6 rounded-2xl bg-[#064E3B] p-5 text-white">
                                    <ShieldCheck
                                        size={24}
                                        className="text-[#A5E8B8]"
                                    />

                                    <h4 className="mt-4 text-sm font-extrabold text-white">
                                        Your Privacy Matters
                                    </h4>

                                    <p className="mt-2 text-xs leading-6 text-white/70">
                                        We are committed to responsible
                                        information handling.
                                    </p>
                                </div>
                            </div>
                        </aside>

                        {/* SECTIONS */}
                        <div className="overflow-hidden rounded-[22px] border border-[#DCEAE1] bg-white shadow-sm">
                            <div className="h-1.5 bg-gradient-to-r from-[#064E3B] via-[#168B43] to-[#A5E8B8]" />

                            <div className="p-6 sm:p-9 lg:p-11">
                                {sections.map((section, index) => (
                                    <PolicySection
                                        key={section.title}
                                        section={section}
                                        index={index}
                                        last={index === sections.length - 1}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-14 sm:py-20">
                <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
                    <div className="relative overflow-hidden rounded-[24px] bg-[#064E3B] px-6 py-14 text-center text-white sm:px-10 sm:py-16">
                        <div className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full border border-white/10" />

                        <div className="relative mx-auto max-w-[680px]">
                            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 text-[#A5E8B8]">
                                <ShieldCheck size={30} />
                            </div>

                            <p className="mt-6 text-[11px] font-extrabold uppercase tracking-[0.17em] text-[#B6F2CB]">
                                Your Privacy
                            </p>

                            <h2 className="mt-3 text-[30px] font-extrabold text-white sm:text-[40px]">
                                Your Privacy Is Important To Us.
                            </h2>

                            <p className="mt-5 text-sm leading-8 text-white/75">
                                We are committed to handling your
                                information responsibly and providing
                                a safe experience throughout your
                                journey with our website.
                            </p>

                            <Link
                                to="/contact-us"
                                className="mt-8 inline-flex items-center gap-3 rounded-xl bg-[#168B43] px-7 py-4 text-sm font-extrabold text-white transition hover:bg-[#20A653]"
                            >
                                Contact Our Team
                                <ArrowRight size={18} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
